import {
  Component,
  OnInit,
  OnDestroy,
  Output,
  EventEmitter,
  Inject,
} from "@angular/core";
import { Router } from "@angular/router";
import { DOCUMENT } from "@angular/common";
import { CookieService } from "ngx-cookie-service";
import { LanguageService } from "../../core/services/language.service";
import { TranslateService } from "@ngx-translate/core";
import { Observable, map, interval, Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { HttpService } from "src/app/core/services/http.service";
import { NgxSpinnerService } from "ngx-spinner";
import { AlertService } from "src/app/core/services/alert.service";

// 푸시 상태 enum
enum PushStatus {
  PENDING = "PENDING",
  SENT = "SENT",
  READ = "READ",
}

// FCM 알림 응답 DTO
interface FcmNotification {
  id: number;
  usersId: number;
  title: string;
  body: string;
  pushStatus: PushStatus;
  type: string;
  image: string;
  dataMap: { [key: string]: any };
  createdAt: string;
  updatedAt: string;
  deletedAt: string;
}

@Component({
  selector: "app-topbar",
  templateUrl: "./topbar.component.html",
  styleUrls: ["./topbar.component.scss"],
})

/**
 * Topbar component
 */
export class TopbarComponent implements OnInit, OnDestroy {
  mode: any;
  element: any;
  cookieValue: any;
  flagvalue: any;
  countryName: any;
  valueset: any;
  theme = "vertical";
  layout: string;
  dataLayout$: Observable<string>;
  // Define layoutMode as a property

  private destroy$ = new Subject<void>();
  private notificationCheckInterval = 30000; // 30초마다 확인

  constructor(
    @Inject(DOCUMENT) private document: any,
    private router: Router,
    public languageService: LanguageService,
    public translate: TranslateService,
    public _cookiesService: CookieService,
    private httpService: HttpService,
    private spinner: NgxSpinnerService,
    private alertService: AlertService
  ) {}

  openMobileMenu: boolean;

  @Output() settingsButtonClicked = new EventEmitter();
  @Output() mobileMenuButtonClicked = new EventEmitter();

  list: FcmNotification[] = [];
  async ngOnInit() {
    await this.loadNotifications();

    // 주기적으로 알림 확인 (30초마다)
    interval(this.notificationCheckInterval)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.loadNotifications();
      });

    this.openMobileMenu = false;
    this.element = document.documentElement;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // 푸시 알림 목록 로드
  async loadNotifications(): Promise<void> {
    try {
      const params = {
        page: 0,
        size: 10, // 최근 10개만 표시
      };

      const response: any = await this.httpService.doGet(
        "/api/users/push-history",
        true,
        params
      );

      this.list = response.content || [];
      console.log("푸시 알림 목록:", this.list);
    } catch (error) {
      console.error("푸시 알림 목록 로드 오류:", error);
    }
  }

  async moveUrl(data: FcmNotification) {
    console.log("data:", data);
    // 알림을 읽음 상태로 변경 (SENT 상태인 경우에만)
    if (data.pushStatus === PushStatus.SENT) {
      try {
        // 알림 상태를 READ로 업데이트하는 API 호출 (필요한 경우)
        // await this.httpService.doPut(`/api/users/push-history/${data.id}/read`);

        // 로컬 상태 업데이트
        data.pushStatus = PushStatus.READ;
      } catch (error) {
        console.error("알림 읽음 상태 변경 오류:", error);
      }
    }
    console.log(data.type);
    // 알림 타입에 따라 해당 페이지로 이동
    const notificationType = data.dataMap?.type ?? data.type;
    const historyId = data.dataMap?.id ?? data.dataMap?.subId;

    switch (notificationType) {
      case "payment":
        // 결제 관련 알림 시
        this.router.navigate(["/payment/payment-history"]);
        break;
      case "autoPayment":
        // 자동결제 신청 알림 시 해당 고객 이용 내역 상세로 이동
        if (historyId) {
          this.router.navigate(["/parking/usage-history"], {
            queryParams: { historyId },
          });
        } else {
          this.router.navigate(["/parking/usage-history"]);
        }
        break;
      case "refund":
        // 환불 관련 알림 시
        if (data.body === "월주차권 신청 완료 되었습니다.") {
          this.router.navigate(["/payment/payment-history"]);
        } else {
          this.router.navigate(["/payment/refund-history"]);
        }
        break;
      case "vehicleRegistrationUploaded":
      case "usageIntention":
        this.router.navigate(["/parking/usage-history"], {
          queryParams: { historyId: data.dataMap.id },
        });
        break;
    }
  }
  setLanguage(text: string, lang: string, flag: string) {
    this.countryName = text;
    this.flagvalue = flag;
    this.cookieValue = lang;
    this.languageService.setLanguage(lang);
  }

  /**
   * Toggles the right sidebar
   */
  toggleRightSidebar() {
    this.settingsButtonClicked.emit();
  }

  /**
   * Toggle the menu bar when having mobile screen
   */
  toggleMobileMenu(event: any) {
    event.preventDefault();
    this.mobileMenuButtonClicked.emit();
  }

  /**
   * Logout the user
   */
  logout() {
    this.router.navigate(["/auth/login"]);
  }

  /**
   * Fullscreen method
   */
  fullscreen() {
    document.body.classList.toggle("fullscreen-enable");
    if (
      !document.fullscreenElement &&
      !this.element.mozFullScreenElement &&
      !this.element.webkitFullscreenElement
    ) {
      if (this.element.requestFullscreen) {
        this.element.requestFullscreen();
      } else if (this.element.mozRequestFullScreen) {
        /* Firefox */
        this.element.mozRequestFullScreen();
      } else if (this.element.webkitRequestFullscreen) {
        /* Chrome, Safari and Opera */
        this.element.webkitRequestFullscreen();
      } else if (this.element.msRequestFullscreen) {
        /* IE/Edge */
        this.element.msRequestFullscreen();
      }
    } else {
      if (this.document.exitFullscreen) {
        this.document.exitFullscreen();
      } else if (this.document.mozCancelFullScreen) {
        /* Firefox */
        this.document.mozCancelFullScreen();
      } else if (this.document.webkitExitFullscreen) {
        /* Chrome, Safari and Opera */
        this.document.webkitExitFullscreen();
      } else if (this.document.msExitFullscreen) {
        /* IE/Edge */
        this.document.msExitFullscreen();
      }
    }
  }

  changeLayout(layoutMode: string) {
    this.theme = layoutMode;
  }

  // 개별 알림 삭제
  async deleteAlarm(notification: FcmNotification, index: number) {
    try {
      await this.httpService.doDelete(
        `/api/users/push-history/${notification.id}`
      );
      this.list.splice(index, 1);
      this.alertService.successmsg("알림이 삭제되었습니다.");
    } catch (error) {
      console.error("알림 삭제 오류:", error);
      this.alertService.errormsg("알림 삭제에 실패했습니다.");
    }
  }

  // 전체 알림 삭제 (최근 10개만 삭제)
  async deleteAll() {
    try {
      // 현재 표시된 알림들을 모두 삭제
      const deletePromises = this.list.map((notification) =>
        this.httpService.doDelete(`/api/users/push-history/${notification.id}`)
      );

      await Promise.all(deletePromises);
      this.list = [];
      this.alertService.successmsg("모든 알림이 삭제되었습니다.");
    } catch (error) {
      console.error("전체 알림 삭제 오류:", error);
      this.alertService.errormsg("알림 삭제에 실패했습니다.");
    }
  }

  // 날짜 포맷팅
  formatDateTime(dateString: string): string {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleString("ko-KR", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  }
}
